import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, computed, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { type CdkDragDrop, CdkDrag, CdkDragHandle, CdkDropList } from '@angular/cdk/drag-drop';
import { type BsModalRef, BsModalService } from 'ngx-bootstrap/modal';
import type { Observable } from 'rxjs';
import { toSignal } from '@angular/core/rxjs-interop';

import type { Uuid } from '@common/types';
import type { WidgetErrorItem } from '@common/types/widget-errors.type';
import { ESelectedEntityType } from '@common/types/layout.type';

import type { GridForm } from '@sites/entity-signal-form';

import type { DenormalizedColumnEntity, DenormalizedGridEntity, WebLegacyWidgetEntity } from '@web/types';

import { WidgetsFacade } from '@web/store/page-structure/widgets/widgets.facade';
import { GridsFacade } from '@web/store/page-structure/grids/grids.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { NavigationDrawerService } from '@web/layout/drawers/navigation-drawer/navigation-drawer.service';
import { WidgetsErrorsFacade } from '@web/store/page-structure/widgets-errors/widgets-errors.facade';
import { BlocksFacade } from '@web/store/page-structure/blocks/blocks.facade';

import { WebGridFormService } from '@web/core/services/web-grid-form.service';
import { GridsService } from '@web/store/page-structure/grids/grids.service';

import { AddWidgetOrGridComponent } from '@web/layout/content/page-content/components/add-widget-or-grid';
import { AddWidgetComponent } from '@web/layout/content/page-content/components/add-widget';
import {
    type HoverState,
    SingleHoverDirective,
} from '@web/layout/content/page-content/directives/single-hover.directive';
import { NestedDropListDirective } from '@web/layout/content/page-content/directives/nested-drop-list.directive';

import { GridControlsComponent } from '@web/layout/content/page-content/components/controls/grid-controls/grid-controls.component';
import { WebGridCellResizeComponent } from '@web/shared/components/web-grid-cell-resize/web-grid-cell-resize.component';

import { GridWidgetComponent } from '@web/layout/content/page-content/components/widgets/grid-widget/grid-widget.component';

@Component({
    selector: 'web-grid',
    templateUrl: './default-grid.component.html',
    styleUrls: ['./default-grid.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        CdkDrag,
        CdkDragHandle,
        CdkDropList,
        GridControlsComponent,
        WebGridCellResizeComponent,
        GridWidgetComponent,
        SingleHoverDirective,
        NestedDropListDirective,
    ],
})
export class DefaultGridComponent implements OnInit {
    public readonly index = input(0);
    public readonly grid = input.required<DenormalizedGridEntity>();
    public readonly blockId = input<string>();
    public readonly column = input<DenormalizedColumnEntity>();
    public readonly hideControls = input(false);

    public hoverState = signal<HoverState>(null);
    public isSettingsMenuOpened = signal(false);

    private readonly modalService = inject(BsModalService);
    private readonly gridsService = inject(GridsService);
    private readonly gridsFacade = inject(GridsFacade);
    private readonly blocksFacade = inject(BlocksFacade);
    private readonly widgetsFacade = inject(WidgetsFacade);
    private readonly widgetsErrorsFacade = inject(WidgetsErrorsFacade);
    private readonly layoutFacade = inject(LayoutFacade);
    private readonly navDrawerService = inject(NavigationDrawerService);
    private readonly gridFormService = inject(WebGridFormService);
    private readonly destroyRef = inject(DestroyRef);

    public form!: GridForm;

    public readonly entityId$: Observable<string> = this.layoutFacade.entityId$;
    public readonly hideControls$: Observable<boolean> = this.layoutFacade.hideControls$;
    public readonly isMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isTablet$: Observable<boolean> = this.layoutFacade.isDeviceTablet$;

    public readonly connectedTo = toSignal(this.blocksFacade.columnConnectedToList$, { initialValue: [] as string[] });

    // Derived computeds
    public readonly variantMap = computed(() => this.gridsService.getVariantLayout(this.form.variant().value()));

    public readonly gridGapX = computed(() => `${this.form.gapXValue().value()}px`);
    public readonly gridGapY = computed(() => `${this.form.gapYValue().value()}px`);

    public readonly cellGridColumns = computed(() =>
        this.form
            .rowCellWidth()
            .value()
            .map((row) => row.map((width) => `span ${width ?? 4}/ span ${width ?? 4}`))
    );

    public ngOnInit(): void {
        this.form = this.gridFormService.getOrCreateScoped(this.grid().id, this.destroyRef);
    }

    // Methods
    public addWidget(cellIndex: number): void {
        const modalRef: BsModalRef<AddWidgetComponent> = this.modalService.show(AddWidgetComponent, {
            backdrop: false,
            class: 'add-widgets-modal',
            initialState: {},
        });

        const grid = this.grid();
        const index = this.index();
        modalRef.content.onClose.subscribe((entity) => {
            if (!entity) {
                return;
            }

            this.widgetsFacade.createWidgetToGrid(grid.parentId, grid.id, cellIndex, index, entity.name);
        });
    }

    public hovered(event: HoverState): void {
        this.hoverState.set(event);
    }

    public setSettingsMenuOpened(state: boolean): void {
        this.isSettingsMenuOpened.set(state);
    }

    public addWidgetOrGrid(): void {
        const modalRef: BsModalRef<AddWidgetOrGridComponent> = this.modalService.show(AddWidgetOrGridComponent, {
            backdrop: false,
            class: 'add-widgets-modal',
            initialState: {},
        });

        modalRef.content.onClose.subscribe((payload) => {
            if (!payload) {
                return;
            }

            const grid = this.grid();
            const index = this.index();

            if (payload.type === 'tab') {
                // TODO: IMPLEMENT
                return;
            }

            if (payload.type === 'grid') {
                this.gridsFacade.create(grid.parentId, payload.variant, index + 1);
                return;
            }

            if (!payload.entity) {
                return;
            }

            this.widgetsFacade.createWidget(grid.parentId, index + 1, payload.entity.name);
        });
    }

    public dropWidget(event: CdkDragDrop<WebLegacyWidgetEntity[]>): void {
        const { item, container, previousContainer, currentIndex, previousIndex } = event;

        if (container === previousContainer && previousIndex === currentIndex) {
            return;
        }

        // if widget was dropped between grids
        if (container.id.startsWith('grid') && previousContainer.id.startsWith('grid')) {
            const [, prevGridId, prevGridGroupIndex] = previousContainer.id.split('_');
            const [, gridId, gridGroupIndex] = container.id.split('_');
            this.gridsFacade.moveWidgetFromGridToGrid(
                item.data.id,
                Number(prevGridGroupIndex),
                Number(gridGroupIndex),
                prevGridId,
                gridId,
                previousIndex,
                currentIndex
            );
            return;
        }

        // if widget was dropped from column to grid
        if (container.id.startsWith('grid')) {
            const [, gridId, gridGroupIndex] = container.id.split('_');
            this.gridsFacade.moveWidgetFromColumnToGrid(
                item.data.id,
                Number(gridGroupIndex),
                previousContainer.id,
                gridId,
                previousIndex,
                currentIndex
            );
        }
    }

    public selectGrid(): void {
        const grid = this.grid();
        const column = this.column();
        this.layoutFacade.setSelectedEntity({ entityType: ESelectedEntityType.grid, entityId: grid.id });
        if (!column) {
            return;
        }

        this.navDrawerService.selectInNavigation({
            entityId: grid.id,
            columnId: column.id,
            sectionId: column.parentId,
            blockId: this.blockId(),
        });
    }

    public deleteGrid(): void {
        this.gridsFacade.remove(this.grid(), this.index());
    }

    public copyGrid(): void {
        this.gridsFacade.copy(this.grid(), this.index());
    }

    public changeCellWidthToLeft(rowIndex: number, leftCellIndex: number, rightCellIndex: number): void {
        this.form.updateRowCellWidth((rowCellWidth) => {
            return rowCellWidth.map((row, rowIdx) => {
                if (rowIdx !== rowIndex) {
                    return row;
                }

                return row.map((cell, cellIdx) => {
                    if (cellIdx === leftCellIndex) {
                        return cell - 1;
                    }

                    if (cellIdx === rightCellIndex) {
                        return cell + 1;
                    }

                    return cell;
                });
            });
        });
    }

    public changeCellWidthToRight(rowIndex: number, leftCellIndex: number, rightCellIndex: number): void {
        this.form.updateRowCellWidth((rowCellWidth) => {
            return rowCellWidth.map((row, rowIdx) => {
                if (rowIdx !== rowIndex) {
                    return row;
                }

                return row.map((cell, cellIdx) => {
                    if (cellIdx === leftCellIndex) {
                        return cell + 1;
                    }

                    if (cellIdx === rightCellIndex) {
                        return cell - 1;
                    }

                    return cell;
                });
            });
        });
    }

    public widgetErrorsById$(id: Uuid): Observable<WidgetErrorItem> {
        return this.widgetsErrorsFacade.widgetErrorsById$(id);
    }

    public cellsLength(row): number {
        return row.length;
    }
}
